/**
 * Click Effect - Fast, impactful click feedback
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';
import { getSurfaceY } from '@/rendering/terrain/TerrainGenerator';

interface ClickFX {
  ring: THREE.Mesh;
  dot: THREE.Mesh;
  age: number;
}

const MAX_EFFECTS = 8;
const DURATION = 0.35; // Fast!

export class ClickEffect {
  private scene: THREE.Scene;
  private effects: ClickFX[] = [];
  private moveColor = new THREE.Color(0x00ff66);
  private attackColor = new THREE.Color(0xff3333);

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    EventBus.on('command:move', (data: { x: number; z: number }) => {
      this.spawn(data.x, data.z, 'move');
    });

    EventBus.on('command:attack', (data: { x?: number; z?: number }) => {
      if (data.x !== undefined && data.z !== undefined) {
        this.spawn(data.x, data.z, 'attack');
      }
    });
  }

  spawn(x: number, z: number, type: 'move' | 'attack' = 'move'): void {
    if (this.effects.length >= MAX_EFFECTS) {
      const old = this.effects.shift()!;
      this.dispose1(old);
    }

    const color = type === 'move' ? this.moveColor : this.attackColor;
    const y = getSurfaceY(x, z) + 0.1;

    // Ring - expands outward
    const ringGeo = new THREE.RingGeometry(0.1, 0.25, 32);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.set(x, y, z);

    // Center dot - bright flash
    const dotGeo = new THREE.CircleGeometry(0.2, 16);
    dotGeo.rotateX(-Math.PI / 2);
    const dotMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.set(x, y + 0.01, z);

    this.scene.add(ring);
    this.scene.add(dot);

    this.effects.push({ ring, dot, age: 0 });
  }

  update(deltaTime: number): void {
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const fx = this.effects[i];
      fx.age += deltaTime;

      if (fx.age >= DURATION) {
        this.dispose1(fx);
        this.effects.splice(i, 1);
        continue;
      }

      const t = fx.age / DURATION;

      // Ring: fast expand, quick fade
      const ringScale = 1 + t * 6; // Expand to 7x
      fx.ring.scale.set(ringScale, 1, ringScale);
      (fx.ring.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.9;

      // Dot: flash then shrink
      const dotScale = t < 0.15 ? 1 + t * 3 : (1 - t) * 1.2;
      fx.dot.scale.setScalar(Math.max(0, dotScale));
      (fx.dot.material as THREE.MeshBasicMaterial).opacity = (1 - t * t) * 1.2; // Quadratic fade
    }
  }

  private dispose1(fx: ClickFX): void {
    this.scene.remove(fx.ring);
    this.scene.remove(fx.dot);
    fx.ring.geometry.dispose();
    fx.dot.geometry.dispose();
    (fx.ring.material as THREE.Material).dispose();
    (fx.dot.material as THREE.Material).dispose();
  }

  dispose(): void {
    this.effects.forEach(fx => this.dispose1(fx));
    this.effects = [];
  }
}
